/**
 * La marca que decide si el aviso de «tus créditos ahora son saldo» vuelve a
 * abrirse (RET-2142).
 *
 * UNA SOLA MARCA, Y ES PERMANENTE. A diferencia del aviso de precios de Meta
 * —que separa «Cerrar» (vuelve al próximo login) de un checkbox «No volver a
 * mostrar»— acá cerrar apaga el aviso para siempre. Es decisión de producto y
 * conviene tenerla escrita, porque la asimetría con el vecino se lee como
 * descuido: este aviso anuncia un hecho consumado —los créditos ya se
 * convirtieron— y la información queda permanentemente disponible en el tab
 * Saldo, así que insistir no agrega nada.
 *
 * 🔴 POR WORKSPACE **Y** USUARIO, no global. Dos personas comparten navegador
 * más seguido de lo que uno esperaría, y el «ya lo vi» de un dueño no puede
 * silenciar el aviso del otro. Un usuario con dos workspaces tiene que verlo
 * en los dos: el saldo convertido es distinto en cada uno.
 *
 * 🔴 SE QUEDA EN EL CLIENTE. La regla del repo de que una bandera persistente
 * sólo se escribe después de que confirme la escritura de backend que
 * representa
 * (docs/solutions/logic-errors/localstorage-flag-set-before-backend-write-
 * confirms.md) NO aplica acá: esta marca no representa ninguna escritura de
 * backend. Es una preferencia sobre un modal, y mandarla a un servidor
 * inventaría un modo de falla donde un corte de red reabre un aviso que el
 * usuario ya cerró.
 *
 * Todo acceso va envuelto: el modo privado de Safari lanza tanto al leer como
 * al escribir, y la degradación correcta es «no hay marca» —el aviso se
 * abre— y no una excepción en el arranque.
 */

const STORAGE_KEY_PREFIX = 'credits_to_balance_notice_';

export type tCreditsToBalanceScope = {
  workspaceId: string;
  userId: string;
};

export type tCreditsToBalanceState = {
  v: 1;
  /** Epoch ms del cierre. Ausente mientras no se haya cerrado nunca. */
  closedAt?: number;
};

export function getCreditsToBalanceStorageKey(
  scope: tCreditsToBalanceScope,
): string {
  return `${STORAGE_KEY_PREFIX}${scope.workspaceId}_${scope.userId}`;
}

function safeGet(key: string): string | null {
  try {
    return window.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    window.localStorage?.setItem(key, value);
  } catch {
    /* sin almacenamiento: el aviso volverá a abrirse, que es la degradación
       correcta frente a romper el arranque */
  }
}

function safeRemove(key: string): void {
  try {
    window.localStorage?.removeItem(key);
  } catch {
    /* limpieza best-effort */
  }
}

function freshState(): tCreditsToBalanceState {
  return { v: 1 };
}

/**
 * Lee el estado guardado.
 *
 * Con cualquiera de los dos ids vacío devuelve «sin marca» SIN tocar el
 * almacenamiento: leer con un id indefinido apuntaría a otra clave, y el
 * llamador concluiría que nunca se cerró. Es el defecto que documenta
 * `late-entity-id-lazy-init-clobbers-persisted-state.md`, y por eso el montaje
 * además gatea por los dos ids — esto es el cinturón, aquello los tirantes.
 */
export function readCreditsToBalanceState(
  scope: tCreditsToBalanceScope,
): tCreditsToBalanceState {
  if (!scope.workspaceId || !scope.userId) return freshState();

  const key = getCreditsToBalanceStorageKey(scope);
  const raw = safeGet(key);
  if (raw === null) return freshState();

  let parsed: unknown = null;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // JSON corrupto: se limpia para que las lecturas siguientes no vuelvan a
    // intentar parsear basura.
    safeRemove(key);
    return freshState();
  }

  if (!parsed || typeof parsed !== 'object') {
    safeRemove(key);
    return freshState();
  }

  const state = parsed as Record<string, unknown>;
  // Hoy no existe una forma anterior, pero un payload ajeno o futuro no puede
  // colarse hasta el booleano que decide si el modal se abre.
  if (state.v !== 1) {
    safeRemove(key);
    return freshState();
  }

  const closedAt =
    typeof state.closedAt === 'number' && Number.isFinite(state.closedAt)
      ? state.closedAt
      : undefined;

  return closedAt === undefined ? { v: 1 } : { v: 1, closedAt };
}

export function isCreditsToBalanceNoticeClosed(
  scope: tCreditsToBalanceScope,
): boolean {
  return readCreditsToBalanceState(scope).closedAt !== undefined;
}

/**
 * Marca el aviso como cerrado.
 *
 * Lee fresco justo antes de escribir, en vez de partir de una copia en
 * memoria: una pestaña estacionada no puede resucitar un estado que otra ya
 * movió. Misma regla que `NewsCard.storage`.
 */
export function markCreditsToBalanceNoticeClosed(
  scope: tCreditsToBalanceScope,
): void {
  if (!scope.workspaceId || !scope.userId) return;

  const current = readCreditsToBalanceState(scope);
  if (current.closedAt !== undefined) return;

  safeSet(
    getCreditsToBalanceStorageKey(scope),
    JSON.stringify({ ...current, closedAt: Date.now() }),
  );
}
